import BookOpen from "lucide-react/dist/esm/icons/book-open";
import LogOut from "lucide-react/dist/esm/icons/log-out";
import Volume2 from "lucide-react/dist/esm/icons/volume-2";
import VolumeX from "lucide-react/dist/esm/icons/volume-x";
import Wifi from "lucide-react/dist/esm/icons/wifi";
import WifiOff from "lucide-react/dist/esm/icons/wifi-off";
import { canPlay, type Card, type PlayerSummary, type RoomView } from "../../game";
import { haptic, sound } from "../feedback";
import type { ConnectionStatus } from "../use-game";
import { CardBack, PlayingCard } from "./playing-card";

interface GameBoardProps {
  room: RoomView;
  connection: ConnectionStatus;
  latency: number | null;
  pendingCardId: string | null;
  soundEnabled: boolean;
  hapticsEnabled: boolean;
  onToggleSound: () => void;
  onOpenRules: () => void;
  onPlay: (cardId: string, pileIndex: 0 | 1) => void;
  onChin: () => void;
  onRefresh: () => void;
  onReady: () => void;
  onLeave: () => void;
}

function Stock({ count, owner }: { count: number; owner: string }) {
  return (
    <div className="stock" aria-label={`${owner}: ${count} cartas en el mazo`}>
      {count > 0 ? <CardBack label={`Mazo de ${owner}`} compact /> : <span className="empty-stock" />}
      <strong>{count}</strong>
    </div>
  );
}

function Rack({
  player,
  room,
  own,
  pendingCardId,
  onPlay,
  onInvalid,
}: {
  player: PlayerSummary;
  room: RoomView;
  own: boolean;
  pendingCardId: string | null;
  onPlay: (cardId: string, pileIndex: 0 | 1) => void;
  onInvalid: () => void;
}) {
  return (
    <div className={`rack ${own ? "rack--own" : "rack--rival"}`} aria-label={`Cartas de ${player.name}`}>
      {Array.from({ length: 4 }, (_, index) => {
        const card = player.rack[index] ?? null;
        if (!card) return <span className="rack-slot" key={`slot-${index}`} aria-label="Espacio vacío" />;
        const playablePiles = ([0, 1] as const).filter((pileIndex) => canPlay(card, room.piles[pileIndex].top));
        return (
          <PlayingCard
            key={card.id}
            card={card}
            size="rack"
            interactive={own && room.phase === "playing"}
            pending={pendingCardId === card.id}
            playablePiles={playablePiles}
            onPlay={(pileIndex) => onPlay(card.id, pileIndex)}
            onInvalid={onInvalid}
          />
        );
      })}
    </div>
  );
}

function Score({ player }: { player: PlayerSummary }) {
  return (
    <div className="player-score" aria-label={`${player.chinCount} CHIN acertados`}>
      <strong>{player.name}</strong>
      <span>{player.chinCount} CHIN</span>
    </div>
  );
}

function CenterPile({ card, count, index, hot }: { card: Card | null; count: number; index: 0 | 1; hot: boolean }) {
  return (
    <div className={`center-pile ${hot ? "is-hot" : ""}`} data-pile-index={index}>
      <span className="pile-shadow pile-shadow--two" aria-hidden="true" />
      <span className="pile-shadow pile-shadow--one" aria-hidden="true" />
      {card ? <PlayingCard card={card} size="center" /> : <span className="empty-pile">PILA {index + 1}</span>}
      <small>{count} {count === 1 ? "carta" : "cartas"}</small>
    </div>
  );
}

export function GameBoard({
  room,
  connection,
  latency,
  pendingCardId,
  soundEnabled,
  hapticsEnabled,
  onToggleSound,
  onOpenRules,
  onPlay,
  onChin,
  onRefresh,
  onReady,
  onLeave,
}: GameBoardProps) {
  const self = room.players.find((player) => player.id === room.selfId);
  const rival = room.players.find((player) => player.id !== room.selfId);
  if (!self || !rival) return null;

  const hot = room.chinAvailable;
  const paused = room.phase === "paused" || connection !== "online";
  const finished = room.phase === "finished";
  const winner = room.players.find((player) => player.id === room.winnerId);
  const won = room.winnerId === room.selfId;

  function invalidCard(): void {
    haptic("error", hapticsEnabled);
    sound("error", soundEnabled);
  }

  function playCard(cardId: string, pileIndex: 0 | 1): void {
    haptic("card", hapticsEnabled);
    sound("card", soundEnabled);
    onPlay(cardId, pileIndex);
  }

  function callChin(): void {
    haptic("chin", hapticsEnabled);
    sound(hot ? "chin" : "error", soundEnabled);
    onChin();
  }

  return (
    <main className={`game-shell ${hot ? "has-chin" : ""}`}>
      <header className="game-toolbar">
        <span className="room-mini-code">MESA <b>{room.code}</b></span>
        <span className={`network-pill network-pill--${connection}`} aria-label={connection === "online" ? `Conectado, ${latency ?? 0} milisegundos` : "Sin conexión"}>
          {connection === "online" ? <Wifi aria-hidden="true" size={15} /> : <WifiOff aria-hidden="true" size={15} />}
          {connection === "online" ? `${latency ?? "—"} ms` : "Reconectando"}
        </span>
        <div className="toolbar-actions">
          <button className="icon-button icon-button--table" type="button" onClick={onToggleSound} aria-label={soundEnabled ? "Silenciar sonidos" : "Activar sonidos"}>
            {soundEnabled ? <Volume2 aria-hidden="true" size={20} /> : <VolumeX aria-hidden="true" size={20} />}
          </button>
          <button className="icon-button icon-button--table" type="button" onClick={onOpenRules} aria-label="Ver reglas">
            <BookOpen aria-hidden="true" size={20} />
          </button>
          <button className="icon-button icon-button--table" type="button" onClick={onLeave} aria-label="Abandonar partida">
            <LogOut aria-hidden="true" size={20} />
          </button>
        </div>
      </header>

      <section className="player-zone player-zone--rival" aria-label="Zona del rival">
        <div className="player-zone__meta">
          <Score player={rival} />
          <Stock count={rival.stockCount} owner={rival.name} />
        </div>
        <Rack player={rival} room={room} own={false} pendingCardId={null} onPlay={onPlay} onInvalid={invalidCard} />
      </section>

      <section className="table-center" aria-label="Pilas centrales">
        <div className="center-piles">
          <CenterPile card={room.piles[0].top} count={room.piles[0].count} index={0} hot={hot} />
          <CenterPile card={room.piles[1].top} count={room.piles[1].count} index={1} hot={hot} />
        </div>

        <button
          type="button"
          className={`chin-button ${hot ? "is-live" : ""}`}
          onClick={callChin}
          aria-label={hot ? "Cantar CHIN ahora" : "Cantar CHIN; si te equivocás levantás el pozo"}
          disabled={paused || finished}
        >
          <span>{hot ? "¡AHORA!" : "CANTÁ"}</span>
          <strong>CHIN!</strong>
        </button>

        {room.stalled && !paused ? (
          <button className="stalled-button" type="button" onClick={onRefresh}>
            Sin jugadas · dar vuelta
          </button>
        ) : null}
      </section>

      <section className="player-zone player-zone--self" aria-label="Tu zona">
        <div className="self-instruction"><span>Tocá una carta o arrastrala al centro</span></div>
        <Rack player={self} room={room} own pendingCardId={pendingCardId} onPlay={playCard} onInvalid={invalidCard} />
        <div className="player-zone__meta player-zone__meta--self">
          <Score player={self} />
          <Stock count={self.stockCount} owner="tu mazo" />
        </div>
      </section>

      {paused && !finished ? (
        <div className="state-overlay" role="status" aria-live="assertive">
          <span className="state-overlay__spinner" aria-hidden="true" />
          <strong>{connection === "online" ? "Esperando al rival" : "Recuperando conexión"}</strong>
          <p>La mesa está pausada y tu lugar sigue guardado.</p>
        </div>
      ) : null}

      {finished ? (
        <div className="state-overlay state-overlay--finished" role="dialog" aria-modal="true" aria-labelledby="result-title">
          <span className="result-kicker">PARTIDA TERMINADA</span>
          <strong id="result-title">{room.winnerId === null ? "Empate técnico" : won ? "¡Ganaste!" : `Ganó ${winner?.name ?? "tu rival"}`}</strong>
          <p>{won ? "Te quedaste sin cartas primero." : "La revancha está a un toque."}</p>
          <div className="result-score">
            <span><b>{self.chinCount}</b> tus CHIN</span>
            <i aria-hidden="true" />
            <span><b>{rival.chinCount}</b> CHIN rival</span>
          </div>
          <button className="primary-button" type="button" onClick={onReady}>Jugar revancha</button>
          <button className="text-button" type="button" onClick={onLeave}>Volver al inicio</button>
        </div>
      ) : null}
    </main>
  );
}
